// Data for the analysis screens on a period window (Phase 4C-3). One bounded request per report kind through the shared 4C-0 helpers
// (listReport + callWithFallback + useReportQuery), so the same window is requested once even when several components ask for it, a slower
// answer for a store or range the user already left is dropped, and the cache key carries the token tail, the stores and the range: another
// account or another store never reads these rows.
//
//   const { daily, monthly, compensation, windows, loading, error, reload } = useAnalysisWindow({ kinds, storeIds, range, enabled })
//
// Rows are `null` until the first answer. While a new answer is loading the previous rows are NOT exposed (`daily` etc. are null), so a store switch
// can never show the old store's numbers.
import { useMemo } from "react";
import { callWithFallback, listReport } from "./api/reportQuery";
import { fetchCompsAPI, fetchDailysAPI, fetchMonthlysAPI } from "./api/report";
import useReportQuery, { reportCacheKey } from "./useReportQuery";
import { isWindowAnswer } from "./analysisWindow";

const LEGACY = { daily: fetchDailysAPI, monthly: fetchMonthlysAPI, compensation: (token, stores) => fetchCompsAPI(token, stores) };

// Legacy helpers resolve to an array or undefined; shape it like an http result so the hook handles both the same way.
const legacyResult = (rows) => (Array.isArray(rows)
  ? { ok: true, status: 200, data: { success: true, data: rows, window: null } }
  : { ok: false, status: 0, data: null });

export const fetchAnalysisKind = async (kind, { token, stores, range, signal }) => {
  const { result } = await callWithFallback({
    primary: () => listReport(kind, { stores, from: range.from, to: range.to }, { token, signal }),
    detect: isWindowAnswer,
    fallback: async () => legacyResult(await LEGACY[kind](token, stores)),
  });
  return result;
};

function useKind(kind, { token, stores, range, enabled }) {
  // An empty store list means the caller's whole scope, exactly as the legacy requests; screens that want "no stores" pass enabled=false.
  const active = Boolean(enabled && token);
  const key = active ? reportCacheKey(token, `analysis-${kind}`, { stores: [...stores].sort(), from: range.from, to: range.to }) : "";
  // The answer is stamped with its key: the hook keeps the previous data for one render after the key changes, and that must never be shown.
  const query = useReportQuery(key, async (signal) => {
    const result = await fetchAnalysisKind(kind, { token, stores, range, signal });
    return result.ok && result.data ? { ...result, data: { ...result.data, forKey: key } } : result;
  }, { enabled: active });
  const body = !query.loading && !query.error && query.data && query.data.forKey === key ? query.data : null;
  return {
    rows: body && Array.isArray(body.data) ? body.data : null,
    window: body ? body.window || null : null,
    loading: active && !query.error && !body,
    error: active ? query.error : null,
    reload: query.reload,
  };
}

export default function useAnalysisWindow({ kinds, storeIds, range, enabled = true }) {
  const token = localStorage.getItem("auth-token");
  const stores = useMemo(() => (Array.isArray(storeIds) ? storeIds : []), [storeIds]);
  const wants = (kind) => enabled && kinds.includes(kind);
  const daily = useKind("daily", { token, stores, range, enabled: wants("daily") });
  const monthly = useKind("monthly", { token, stores, range, enabled: wants("monthly") });
  const compensation = useKind("compensation", { token, stores, range, enabled: wants("compensation") });
  const parts = [daily, monthly, compensation].filter((part, index) => wants(["daily", "monthly", "compensation"][index]));

  return {
    daily: daily.rows,
    monthly: monthly.rows,
    compensation: compensation.rows,
    windows: { daily: daily.window, monthly: monthly.window, compensation: compensation.window },
    loading: parts.some((part) => part.loading),
    error: parts.find((part) => part.error)?.error || null,
    reload: () => parts.forEach((part) => part.reload()),
  };
}
